import {
  ProductType,
  updateQs,
  useBulkQuotation,
  useAuthenticated,
  useDefaultCouponCode,
  useLocalStorage,
  useProductSumAssuredRangeMapping,
  useRetrieveBestOffer,
  useVariant,
} from '@bowtie-web/common';
import { Box } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Skeleton } from '@material-ui/lab';
import { pick } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { APP_START_URL_LOCAL_STORAGE_KEY, InsuredTarget } from '../constants';
import { useAttributeAndQsContext } from '../contexts/AttributeAndQsContext';
import { useQuotationContext } from '../contexts/QuotationContext';
import { useUserData } from '../contexts/UserAuthContext';
import { isSumAssuredRequired } from '../helpers';
import { getAppStartPath, sortAndStringifyQs } from '../helpers/queryparam';
import theme from '../helpers/theme';
import useCouponOptions from '../hooks/useCouponOptions';
import useReferralCouponCode from '../hooks/useReferralCouponCode';
import useRidersSelection from '../hooks/useRidersSelection';
import useTotalPremiumComparison from '../hooks/useTotalPremiumComparison';
import getLogger from '../logging';
import ActionPanel from './ActionPanel/ActionPanel';
import CouponForm from './CouponForm';
import ErrorMessagePanel from './ErrorMessagePanel';
import FormPanel from './FormPanel/FormPanel';
import InsuredTargetToggle from './InsuredTargetToggle/InsuredTargetToggle';
import RecommendationAnchor from './MarketingPageAnchorLink';
import { ANCHOR_LINK_VARIANT_MAPPINGS } from './MarketingPageAnchorLink/MarketingPageAnchorLink';
import PromotionBanner from './PromotionBanner/PromotionBanner';
import ResultPanel from './ResultPanel';
import RidersSelectionPanel from './RidersSelectionPanel/RidersSelectionPanel';
import TermAndConditions from './TermAndConditions';
import VariableAttributeButtons from './VariableAttributeButtons/VariableAttributeButtons';
import { getInitialCouponCode, getShouldUseBestOffer } from './bestOffer';
import {
  getCanOfferInsuredTarget,
  getInsuredTarget,
  getInsuredTargetCouponCode,
  isUnknownInsuredTarget,
  parseInsuredTarget,
} from './insuredTarget';

const logger = getLogger('quotation');

const QuotationWidget = () => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const {
    activeProduct,
    products: productsBase,
    variables,
    values,
    handleChange,
    selectedVariable,
    setActiveProduct,
    setSelectedVariable,
    isIssueAgeValid,
    coupon,
    setCoupon,
    isSumAssuredValid,
    bulkQuotation,
    activeProductQuotation,
    productType,
    socialProof,
    rawProducts,
    enableShowingAnnualLevelTermLife,
    termLifeYear,
    setTermLifeYear,
  } = useQuotationContext();

  const allQs = useAttributeAndQsContext();
  const {
    riderOptions,
    button,
    size: sizeProp = 'responsive',
    showRecommendationAnchor,
    shouldShowBanner,
    variableAttribute,
    shouldShowVariableAttributeButtons,
    anchorLinkType,
    defaultCoupon,
    defaultCouponType,
    publicDefaultCouponTypes,
    existingCustomerDefaultCouponTypes,
    defaultRiderOption,
    insuredTarget: insuredTargetQs,
  } = allQs;

  // Use isExistingCustomer from postMessage instead of query param
  const { isExistingCustomer } = useUserData();
  const isLargeInResponsive = useMediaQuery(theme.breakpoints.up('sm'));

  const size = useMemo(
    () =>
      sizeProp === 'responsive'
        ? isLargeInResponsive
          ? 'large'
          : 'small'
        : sizeProp,
    [isLargeInResponsive, sizeProp],
  );

  // A temporary method to filter BB-CI or Juvenile CI if there's regular CI product
  const products = useMemo(() => {
    const regularCiProductCodes = ['term-ci', 'term-ci-plus', 'term-ci-max'];
    const regularTermCiIncludedInDisplayProducts = productsBase.some(
      ({ code }) => regularCiProductCodes.includes(code),
    );
    if (!regularTermCiIncludedInDisplayProducts) {
      return productsBase;
    }
    const bbCiOrJuvenileCiProductCodes = ['bb-ci', 'juvenile-ci'];
    return productsBase.filter(
      ({ code }) => !bbCiOrJuvenileCiProductCodes.includes(code),
    );
  }, [productsBase]);

  const { riders, selectedRiders, onSelectedRiderChange } = useRidersSelection({
    productCodes: products.map(({ code }) => code),
    riderCodes: riderOptions,
    activeProduct: activeProduct?.code,
    disable: !riderOptions,
    multiple: false,
    defaultRiderOption,
  });

  const demographicData = useMemo(
    () => pick(values, ['age', 'gender', 'smoker']),
    [values],
  );

  const [riderSumAssuredRanges] = useProductSumAssuredRangeMapping(
    riders,
    values.age,
  );

  const [riderDetailAndQuotations, { loading: loadingRidersQuotation }] =
    useBulkQuotation(
      {
        products: riders.map(code => {
          // check the ranges
          return {
            code,
            sum_assured:
              riderSumAssuredRanges &&
              riderSumAssuredRanges.size > 0 &&
              isSumAssuredRequired(code)
                ? // For now the only use case of rider sum_assured is last wish, where min = max
                  riderSumAssuredRanges.get(code)?.max_sum_assured
                : undefined,
          };
        }),
        ...demographicData,
        coupon_code: coupon?.promo_code,
      },
      { skip: !activeProduct },
    );

  const appStartUrl = useMemo(() => {
    if (!activeProduct) {
      return '';
    }
    /**
     * This generates the following product query
     * e.g.
     * ?products[0][sum_assured]=200000&products[1][product_id]=last-wish-rider&products[1][sum_assured]=300000
     */
    const productQs = [activeProduct.code, ...selectedRiders].reduce(
      (a, v, i) => ({
        ...a,
        [`products[${i}][product_id]`]: v,
        ...(isSumAssuredRequired(v) && {
          [`products[${i}][sum_assured]`]:
            i === 0
              ? values.sumAssured
              : riderSumAssuredRanges.get(v)?.max_sum_assured,
        }),
      }),
      {},
    );

    const qs = sortAndStringifyQs({
      coupon: coupon ? coupon.promo_code : '',
      ...values,
      ...allQs,
      ...productQs,
      // termLifeYear is for quotation FE use only
      termLifeYear: undefined,
      // remove the sum assured from the query string
      sumAssured: undefined,
    });

    return getAppStartPath(qs, intl.locale);
  }, [
    activeProduct,
    allQs,
    coupon,
    intl.locale,
    riderSumAssuredRanges,
    selectedRiders,
    values,
  ]);

  const termAndConditionProducts = [
    ...products,
    ...riderDetailAndQuotations.map(detail => detail.product),
  ];

  const onClickCta = useCallback(() => {
    if (appStartUrl) {
      window.open(appStartUrl, '_blank');
    }
  }, [appStartUrl]);

  const [, setUrl] = useLocalStorage(
    APP_START_URL_LOCAL_STORAGE_KEY,
    appStartUrl,
  );

  const handleVariableAttributeChange = (params: Record<string, string>) => {
    history.push({
      ...location,
      search: updateQs(location.search, {
        ...params,
      }),
    });
  };

  const [defaultCouponCode, { loading: loadingDefaultCouponCode }] =
    useDefaultCouponCode(defaultCoupon, defaultCouponType);

  // Still read by the widget's own coupon menu, which lists per-embed slot
  // types. The best-offer endpoint names its own slots and takes none.
  const publicCouponTypes = useMemo(
    () => publicDefaultCouponTypes?.split(',') || [],
    [publicDefaultCouponTypes],
  );

  const existingCustomerCouponTypes = useMemo(
    () =>
      existingCustomerDefaultCouponTypes
        ? existingCustomerDefaultCouponTypes.split(',')
        : [],
    [existingCustomerDefaultCouponTypes],
  );

  const [publicCodesMapping] = useCouponOptions(
    products.map(({ code }) => code),
    publicCouponTypes,
  );

  const [existingCustomerCodesMapping] = useCouponOptions(
    products.map(({ code }) => code),
    existingCustomerCouponTypes,
  );

  const isAuthenticated = useAuthenticated();
  const { value: isBestOfferRolloutEnabled } = useVariant('enable_best_offer');
  const shouldUseBestOffer = getShouldUseBestOffer(
    isAuthenticated,
    isBestOfferRolloutEnabled,
  );

  const [bestOffer, { settled: bestOfferSettled }] = useRetrieveBestOffer({
    skip: !shouldUseBestOffer,
    productCode: activeProduct?.code,
  });

  const { value: isInsuredTargetEnabled } = useVariant(
    'enable_quotation_family_referral',
  );
  const canOfferInsuredTarget = getCanOfferInsuredTarget(
    productType === ProductType.VHIS,
    isAuthenticated,
    isInsuredTargetEnabled,
  );
  const [referralCode, { loading: loadingReferralCode }] =
    useReferralCouponCode({ skip: !canOfferInsuredTarget });
  const [selectedInsuredTarget, setSelectedInsuredTarget] = useState(() => {
    if (isUnknownInsuredTarget(insuredTargetQs)) {
      logger.submit(
        `Unknown insuredTarget query value: ${JSON.stringify(insuredTargetQs)}`,
      );
    }
    return parseInsuredTarget(insuredTargetQs);
  });
  const insuredTarget = getInsuredTarget(
    canOfferInsuredTarget,
    referralCode,
    selectedInsuredTarget,
  );

  const handleInsuredTargetChange = useCallback(
    (value: InsuredTarget) => {
      // Drops the applied offer so its prices never show under the other target.
      setCoupon(undefined);
      setSelectedInsuredTarget(value);
    },
    [setCoupon],
  );

  const initialCouponCode = useMemo(
    () =>
      getInsuredTargetCouponCode(
        insuredTarget,
        referralCode,
        getInitialCouponCode(shouldUseBestOffer, bestOffer, defaultCouponCode),
      ),
    [
      insuredTarget,
      referralCode,
      shouldUseBestOffer,
      bestOffer,
      defaultCouponCode,
    ],
  );

  const showTermLifeSlider = useMemo(
    () =>
      productType === ProductType.LIFE && !!enableShowingAnnualLevelTermLife,
    [productType, enableShowingAnnualLevelTermLife],
  );

  const shouldShowNonTermLifeAnchorLink = useMemo(
    () =>
      anchorLinkType !== 'term-level-life-premium-explanation' &&
      (showRecommendationAnchor === 'true' ||
        Object.keys(ANCHOR_LINK_VARIANT_MAPPINGS).includes(anchorLinkType)),
    [anchorLinkType, showRecommendationAnchor],
  );

  const {
    percentageDifference: termLevelLifePremiumPercentageDifference,
    totalPremiums: termLifePremiumsTotal,
  } = useTotalPremiumComparison(
    activeProduct?.code || '',
    'term-life',
    {
      ...demographicData,
      projection_years: Math.max(1, (activeProduct?.renewal_interval ?? 1) - 1),
      sum_assured: values.sumAssured,
    },
    !activeProduct?.code.includes('term-level-life'),
  );

  const productToBadgeMapping = useMemo(() => {
    if (!activeProduct) {
      return undefined;
    }

    const activeProductTotalPremium =
      termLifePremiumsTotal?.[activeProduct.code];
    const termLifeTotalPremium = termLifePremiumsTotal?.['term-life'];

    if (
      termLevelLifePremiumPercentageDifference &&
      activeProductTotalPremium &&
      termLifeTotalPremium &&
      activeProductTotalPremium < termLifeTotalPremium
    ) {
      return {
        productCode: activeProduct.code,
        badge: (
          <FormattedMessage
            id="level-term-life-price-difference.message"
            values={{
              percentage: termLevelLifePremiumPercentageDifference,
            }}
          />
        ),
      };
    }

    if (!socialProof) {
      return undefined;
    }

    return {
      productCode: socialProof.product_code,
      badge: (
        <FormattedMessage
          id="social-proof-message"
          values={{
            percentage: socialProof.comparison_result,
          }}
        />
      ),
    };
  }, [
    activeProduct,
    socialProof,
    termLevelLifePremiumPercentageDifference,
    termLifePremiumsTotal,
  ]);

  useEffect(() => {
    setUrl(appStartUrl);
  }, [appStartUrl, setUrl]);

  return (
    <Box
      width="100%"
      maxWidth={size === 'small' ? 450 : 800}
      minWidth={size === 'small' ? 0 : 600}
    >
      {shouldShowVariableAttributeButtons === 'true' && (
        <VariableAttributeButtons
          size={size}
          variableAttribute={variableAttribute}
          onClick={handleVariableAttributeChange}
        />
      )}
      <Box p={size === 'small' ? 2 : 3} bgcolor="white">
        <PromotionBanner
          promotion={coupon}
          collapse={shouldShowBanner !== 'true'}
          size={size}
        />
        <Box display="grid" alignItems="stretch" gridGap={16}>
          {insuredTarget && (
            <Box px={1}>
              <InsuredTargetToggle
                value={insuredTarget}
                onChange={handleInsuredTargetChange}
              />
            </Box>
          )}
          <FormPanel
            activeProduct={activeProduct}
            rawProducts={rawProducts}
            variables={variables}
            formValues={values}
            onFormValuesChange={handleChange}
            selectedVariable={selectedVariable}
            onSelectVariable={setSelectedVariable}
            setActiveProduct={setActiveProduct}
            onVariableAttributeChange={handleVariableAttributeChange}
            showTermLifeSlider={showTermLifeSlider}
            termLifeYear={termLifeYear}
            onTermLifeYearChange={setTermLifeYear}
            anchorLinkType={anchorLinkType}
          />

          {shouldShowNonTermLifeAnchorLink &&
            (activeProduct ? (
              <RecommendationAnchor
                productType={activeProduct.type}
                variant={
                  showRecommendationAnchor === 'true'
                    ? 'recommendation'
                    : anchorLinkType
                }
              />
            ) : (
              <Skeleton width="250px" variant="text" />
            ))}

          {activeProduct ? (
            <ResultPanel
              onActiveProductChange={setActiveProduct}
              activeProduct={activeProduct}
              formValues={values}
              coupon={coupon}
              products={products}
              size={size}
              isValid={isIssueAgeValid && isSumAssuredValid}
              bulkQuotation={bulkQuotation}
              productToBadgeMapping={productToBadgeMapping}
            />
          ) : (
            <Skeleton
              width="100%"
              height="200px"
              variant="rect"
              style={{ borderRadius: 8 }}
            />
          )}

          {riderOptions && riders.length > 0 && (
            <RidersSelectionPanel
              size={size}
              riderDetailAndQuotations={riderDetailAndQuotations}
              selectedRiders={selectedRiders}
              onSelect={onSelectedRiderChange}
              isValid={isIssueAgeValid && isSumAssuredValid}
              loading={loadingRidersQuotation}
            />
          )}

          {/* Waits for the offer: the field takes its value on mount only. */}
          {activeProduct &&
          !loadingDefaultCouponCode &&
          !loadingReferralCode &&
          (!shouldUseBestOffer || bestOfferSettled) ? (
            <>
              <CouponForm
                key={insuredTarget}
                couponCode={initialCouponCode}
                coupon={coupon}
                onCouponChange={setCoupon}
                isIssueAgeValid={isIssueAgeValid}
                activeProduct={activeProduct}
                publicCouponOptions={publicCodesMapping[activeProduct?.code]}
                existingCustomerCouponOptions={
                  isExistingCustomer && insuredTarget !== 'family'
                    ? existingCustomerCodesMapping[activeProduct?.code]
                    : undefined
                }
                productCodes={products.map(({ code }) => code)}
              />
            </>
          ) : (
            <Skeleton
              width="100%"
              height="95px"
              variant="rect"
              style={{ borderRadius: 4 }}
            />
          )}

          {activeProduct && (
            <ErrorMessagePanel
              formValues={values}
              isIssueAgeValid={isIssueAgeValid}
            />
          )}

          <ActionPanel
            activeProduct={activeProduct}
            formValues={values}
            disabled={
              !isIssueAgeValid ||
              !isSumAssuredValid ||
              loadingRidersQuotation ||
              !activeProduct ||
              !activeProductQuotation ||
              !appStartUrl
            }
            size={size}
            variant={
              button === '2'
                ? productType === ProductType.VHIS
                  ? 'plans-comparison'
                  : 'plan-detail'
                : 'cta-only'
            }
            onClick={onClickCta}
          />

          <TermAndConditions
            products={termAndConditionProducts}
            socialProof={socialProof}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default QuotationWidget;
